import { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '@pazportal/ui';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';

const STORAGE_KEY = 'pazportal_onboarding_complete';

/** Mirrors config/navigation.ts — one entry per real navigation item. */
const NAV_TOUR: Array<{ labelKey: MessageKey; descKey: MessageKey }> = [
  { labelKey: 'nav.dashboard', descKey: 'onboarding.dashboard' },
  { labelKey: 'nav.locations', descKey: 'onboarding.locations' },
  { labelKey: 'nav.trends', descKey: 'onboarding.trends' },
  { labelKey: 'nav.tickets', descKey: 'onboarding.tickets' },
  { labelKey: 'nav.documents', descKey: 'onboarding.documents' },
  { labelKey: 'nav.billing', descKey: 'onboarding.billing' },
];

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Storage blocked: never nag on every load.
    return true;
  }
}

export function OnboardingModal() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!readDismissed()) setVisible(true);
  }, []);

  // Focus in on open, Tab kept inside, Escape closes, focus restored on close.
  useFocusTrap(dialogRef, visible, {
    onClose: () => dismiss(),
    initialFocusRef: primaryRef,
  });

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* per-viewer convenience only */
    }
    setVisible(false);
  }

  if (!visible) return null;

  const steps: Array<{ title: string; body: React.ReactNode }> = [
    {
      title: t('onboarding.welcome'),
      body: <p className="text-sm text-text-muted">{t('onboarding.welcome_body')}</p>,
    },
    {
      title: t('onboarding.navigate'),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">{t('onboarding.navigate_body')}</p>
          <ul className="space-y-2.5">
            {NAV_TOUR.map(({ labelKey, descKey }) => (
              <li key={labelKey} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                />
                <div>
                  <p className="text-sm font-medium text-text">{t(labelKey)}</p>
                  <p className="text-xs text-text-muted">{t(descKey)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      title: t('onboarding.ready'),
      body: <p className="text-sm text-text-muted">{t('onboarding.ready_body')}</p>,
    },
  ];
  const isLast = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/60" onClick={dismiss} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-xl border border-border bg-surface shadow-2xl sm:mx-4 sm:max-w-md sm:rounded-xl"
      >
        <div className="px-4 pb-4 pt-5 sm:px-6 sm:pt-6">
          <p className="text-xs text-text-faint">
            {t('onboarding.step', { n: step + 1, total: steps.length })}
          </p>
          <h2 id="onboarding-title" className="mb-4 mt-1 text-lg font-semibold text-text">
            {steps[step].title}
          </h2>
          {steps[step].body}
        </div>

        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-divider bg-surface px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
          <div className="flex flex-shrink-0 gap-1.5" aria-hidden="true">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full transition-colors ${
                  i === step ? 'bg-primary' : i < step ? 'bg-primary-soft' : 'bg-surface-3'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={dismiss}
              className="min-h-11 rounded-md px-3 text-sm text-text-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t('onboarding.skip')}
            </button>
            <button
              ref={primaryRef}
              type="button"
              onClick={isLast ? dismiss : () => setStep((s) => s + 1)}
              className="min-h-11 rounded-md bg-primary px-4 text-sm font-medium text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {isLast ? t('onboarding.start') : t('onboarding.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
